












































































import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { includes, mapValues, omit } from 'lodash';
import ZenButton from '@reciprocity/button';
import SummaryItem from './components/SummaryItem.vue';
import ClipboardImport from './components/ClipboardImport.vue';
import { ButtonState, CleanSummaryItem, DirtySummaryItem, RecordCount, State } from './types';
import { postRequest, ZenError } from '../../utils/client/client';

const STATES: { [key in State]: ButtonState } = {
  [State.Select]: {
    text: 'Choose CSV file to import',
    showGenerateTemplate: true,
  },
  [State.Analysing]: {
    text: 'Analyzing...',
    disabled: true,
  },
  [State.Import]: {
    text: 'Import data',
    showReset: true,
    successBtn: false,
  },
  [State.ImportHasErrors]: {
    text: 'Import data anyway',
    showReset: true,
    successBtn: true,
  },
  [State.ClipboardImportHasErrors]: {
    text: 'Import data anyway',
    showReset: true,
    successBtn: true,
  },
  [State.Importing]: {
    text: 'Importing...',
    disabled: true,
  },
  [State.DataPreview]: {
    text: 'Import data',
    showGenerateTemplate: false,
    showReset: true,
  },
  [State.ClipboardImport]: {
    text: 'Import data',
    showGenerateTemplate: false,
    showReset: true,
    successBtn: true,
  },
};

const IMPORT_URL = '/_service/import_csv';

// Transforms SummaryItem object so row_errors and block_errors are merged into
// errors, while row_warnings and block_warnings are merged into warnings.
const cleanUpSummary = (summary: DirtySummaryItem[]) =>
  summary.map((item: DirtySummaryItem) => ({
    ...omit(item, ['block_errors', 'block_warnings', 'row_errors', 'row_warnings']),
    warnings: item.block_warnings.concat(item.row_warnings),
    errors: item.block_errors.concat(item.row_errors),
  })) as CleanSummaryItem[];

export default Vue.extend({
  name: 'Import',
  components: {
    ZenButton,
    SummaryItem,
    ClipboardImport,
  },
  props: {
    extraHeaders: {
      type: Object,
      default: () => {},
    },
    hideGenerateTemplate: {
      type: Boolean,
      default: false,
    },
    isExternalRequest: {
      type: Boolean,
      default: false,
    },
    allowedObjectType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      currentState: <State>State.Select,
      filename: <string>'',
      importSummary: <CleanSummaryItem[]>[],
      formData: <FormData>new FormData(),
      finalCount: <RecordCount | null>null,
      showDataPreview: <boolean>false,
      importBtnDisabled: <boolean>true,
    };
  },
  computed: {
    ...mapGetters({
      csrfToken: 'csrfToken',
    }),
    buttonData(): ButtonState {
      return STATES[this.currentState];
    },
    showLoading(): boolean {
      return includes([State.Analysing, State.Importing], this.currentState);
    },
    showClipboardImport(): boolean {
      return includes([State.Select, State.DataPreview], this.currentState);
    },
  },
  methods: {
    ...mapActions({
      showModal: 'modals/show',
      showAlert: 'alerts/show',
    }),
    dataPreviewToggle(state: boolean): void {
      if (state) {
        this.currentState = State.DataPreview;
        this.showDataPreview = true;
      } else {
        this.currentState = State.Select;
        this.showDataPreview = false;
      }
    },
    importBtnToggle(disabled: boolean): void {
      this.importBtnDisabled = disabled;
    },
    handleButtonClick() {
      if (this.currentState === State.Select) {
        this.openFileDialog();
      } else if (
        this.currentState === State.Import ||
        this.currentState === State.ImportHasErrors ||
        this.currentState === State.ClipboardImport ||
        this.currentState === State.ClipboardImportHasErrors
      ) {
        this.confirmImport();
      } else if (this.currentState === State.DataPreview) {
        this.testImport();
      }
    },
    openFileDialog() {
      (this.$refs.fileUpload as HTMLElement).click();
    },
    testImport() {
      this.finalCount = null;
      const sourceState = this.currentState;

      if (this.showDataPreview) {
        this.currentState = State.Importing;
        if (!this.generateClipboardFormData()) {
          return false;
        }
        this.dataPreviewToggle(false);
      } else {
        this.generateFormData();
      }
      this.currentState = State.Analysing;

      this.doPostRequest()
        .then((data: DirtySummaryItem[]) => {
          const summary = cleanUpSummary(data);
          const hasErrors = summary.some(block => Boolean(block.errors.length || block.warnings.length));

          this.importSummary = summary;
          if (sourceState === State.DataPreview) {
            this.currentState = hasErrors ? State.ClipboardImportHasErrors : State.ClipboardImport;
          } else {
            this.currentState = hasErrors ? State.ImportHasErrors : State.Import;
          }
        })
        .catch((err: ZenError) => {
          this.resetPage(true);

          const genericError = {
            type: 'error',
            message: 'Something went wrong.',
          };
          const error = err.response.data || genericError;
          this.showAlert(error);
        });
    },
    confirmImport() {
      this.currentState = State.Importing;

      this.doPostRequest()
        .then((data: DirtySummaryItem[]) => {
          this.importSummary = [];
          this.finalCount = data.reduce(
            (prev: RecordCount, curr: DirtySummaryItem) => mapValues(prev, (value, key) => value + curr[key]),
            { created: 0, updated: 0, deleted: 0, ignored: 0 }
          );
          this.$emit('imported', this.finalCount);
        })
        .catch(() =>
          this.showAlert({
            type: 'error',
            message: 'Something went wrong.',
          })
        )
        .then(this.resetPage);
    },
    doPostRequest() {
      return postRequest({
        url: IMPORT_URL,
        data: this.formData,
        headers: {
          ...this.extraHeaders,
          'X-test-only': this.currentState === State.Analysing,
          'X-CSRFToken': this.csrfToken,
          'X-requested-by': 'gGRC',
        },
      });
    },
    resetPage(hard: boolean = false) {
      this.currentState = State.Select;
      this.importSummary = [];
      this.formData = new FormData();

      // resetPage might get called after the component was already unmounted so
      // fileUpload ref might be undefinned
      if (this.$refs.fileUpload != null) {
        (this.$refs.fileUpload as HTMLInputElement).value = '';
      }
      if (hard) {
        this.filename = '';
        this.finalCount = null;
      }
      this.showDataPreview = false;
      this.$refs.clipboardImport.reset();
    },
    handleResetClick() {
      this.currentState === State.ClipboardImportHasErrors || this.currentState === State.ClipboardImport
        ? this.dataPreviewToggle(true)
        : this.resetPage(true);
    },
    generateClipboardFormData() {
      let importCSV = this.$refs.clipboardImport.getImportCSV();
      if (!importCSV) {
        return false;
      }
      let csvContent = '\ufeff' + importCSV;
      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8',
      });
      const formData = new FormData();
      formData.append('file', blob, 'clipboard.csv');
      this.formData = formData;
      return true;
    },
    generateFormData() {
      const element = this.$refs.fileUpload as HTMLInputElement;
      const files = element.files as FileList;
      const file = files[0] as File;
      const formData = new FormData();
      formData.append('file', file);
      this.formData = formData;
      this.filename = file.name;
    },
    async handleGenerateTemplateClick() {
      try {
        await this.showModal({ type: 'TemplateDownload' });
      } catch (e) {
        // User canceled modal
      }
    },
  },
});
